<template>
  <!-- <Loader v-if="subscriptionStore.isLoading"/> -->
  <div class="my-subscriptions">
    <!-- Кнопка домой -->
    <HomeButton />
    
    <div class="my-subscriptions__top-row my-subscriptions-top-row">
        <div class="my-subscriptions-top-row__title">
            <span class="my-subscriptions-top-row__title-text">Мои подписки</span>
        </div>
        
        <div class="my-subscriptions-top-row__having-subs">
            <span v-if="state.subscriptions && !isDataLoading">{{ formatSubscriptionCount(state.subscriptions.length) }}</span>
        </div>

        <div class="my-subscriptions-top-row-subs-list">
            <div
                v-if="isTelegramApp"
                class="my-subscriptions-top-row-subs-card my-subscriptions-top-row-subs-card--policy"
                @click="openPrivacyPolicyModal"
            >
                <div class="my-subscriptions-top-row-subs-card-row my-subscriptions-top-row-subs-card-row-main-info">
                    <span class="my-subscriptions-top-row-subs-card-row__id">Политика конфиденциальности</span>
                </div>
                <Arrow />
            </div>

            <div 
                class="my-subscriptions-top-row-subs-card" 
                v-for="sub in state.subscriptions"
                :key="sub.id"
                @click="goToSubscription(sub.id)">
                <div class="my-subscriptions-top-row-subs-card-row my-subscriptions-top-row-subs-card-row-main-info">
                    <span class="my-subscriptions-top-row-subs-card-row__id">id: {{ sub.id }} </span>
                    <span class="my-subscriptions-top-row-subs-card-row__exp">до {{ formatDateToRussian(new Date(sub.exp_date)) }}</span>
                </div>

                <div class="my-subscriptions-top-row-subs-card-row my-subscriptions-top-row-subs-card-row-sub-info">
                    <div class="subscription-actions">
                        <BaseButton 
                            v-if="isSubscriptionExpiringSoon(sub.exp_date)"
                            class="my-subscriptions-top-row-subs-card-row__info-warning-card" 
                            text="ПОРА ПРОДЛЕВАТЬ" 
                            styles="primary-gradient"
                            vibration="success"
                            @on-submit="(e) => { e.stopPropagation(); extendSubscription(sub) }"/>
                        <BaseButton 
                            v-if="sub.server.protocol === 'xray'"
                            text="НАСТРОИТЬ" 
                            styles="secondary-100"
                            vibration="button"
                            @on-submit="(e) => { e.stopPropagation(); goToSetup(sub.id) }"/>
                    </div>
                    <Arrow />
                </div>
            </div>
        </div>
    </div>

    <div class="my-subscriptions__botton-row my-subscriptions-bottom-row">
        <span class="my-subscriptions-bottom-row__btn" @click="addNewSubscription">Добавить новую подписку +</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useSubscriptionStore } from '@/entities/Subscription/model/subscriptionStore';
import type { Subscription } from '@/entities/Subscription/model/types';
import { formatDateToRussian } from '@/shared/utils/date/formatDateToRussian';
import { isSubscriptionExpiringSoon } from '@/shared/utils/date/isSubscriptionExpiringSoon';
import { formatSubscriptionCount } from '@/shared/utils/text/pluralize';
import BaseButton from '@/ui/components/Base/BaseButton.vue';
import HomeButton from '@/ui/components/HomeButton.vue';
import Loader from '@/ui/components/Loader.vue';
import { useVibration } from '@/shared/utils/system/vibration';
import { computed, onMounted, reactive, type ComputedRef, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import Arrow from '@/assets/icons/arrow.svg'
import { SentryService } from '@/shared/lib/sentry/sentryService'
import { usePolicyStore } from '@/entities/Policy/model/policyStore'
import { useModal } from '@/shared/lib/modal/useModal'
import { getTelegramInitData } from '@/shared/lib/auth/telegramAuth'

const subscriptionStore = useSubscriptionStore()
const policyStore = usePolicyStore()
const router = useRouter()
const { openModal } = useModal()
const { vibratePattern } = useVibration()

interface State {
  subscriptions: ComputedRef<Subscription[] | null>,
}

const state = reactive<State>({
  subscriptions: computed(() => subscriptionStore.subscriptions)
})

// Общее состояние загрузки
const isDataLoading = computed(() => {
    return subscriptionStore.isLoading
})

const isTelegramApp = computed(() => {
  const tg = (window as any).Telegram?.WebApp

  if (!tg) {
    return false
  }

  const { initData } = getTelegramInitData()
  return Boolean(initData && initData.length > 0)
})

async function openPrivacyPolicyModal() {
  const policyHtml = await policyStore.fetchPolicy()

  openModal({
    content: policyHtml || '<p>Не удалось загрузить политику конфиденциальности.</p>',
    showCloseButton: true
  })
}

async function goToSubscription(subscriptionId: string) {
  // Добавляем вибрацию при выборе подписки
  await vibratePattern('button')
  
  // Отправляем отчет в Sentry
  SentryService.trackUserAction('Переход к подписке', {
    subscription_id: subscriptionId,
    is_extension_flow: sessionStorage.getItem('isExtensionFlow') === 'true'
  })
  
  // Проверяем, пришел ли пользователь для продления подписки
  const isExtensionFlow = sessionStorage.getItem('isExtensionFlow')
  
  if (isExtensionFlow === 'true') {
    console.log('📋 Extension flow: выбрана подписка для продления:', subscriptionId)
    
    // Находим выбранную подписку
    const selectedSubscription = state.subscriptions?.find(sub => sub.id === subscriptionId)
    
    if (selectedSubscription) {
      // Устанавливаем текущую подписку для продления
      subscriptionStore.setCurrentSubscription(selectedSubscription)
      
      // Очищаем флаг
      sessionStorage.removeItem('isExtensionFlow')
      
      // Переходим на страницу оплаты для продления
      sessionStorage.setItem('purchaseType', 'extension')
      sessionStorage.setItem('cameFromHome', 'true')
      router.push('/payment')
    }
  } else {
    console.log('📋 Normal flow: переход к деталям подписки:', subscriptionId)
    // Обычный поток - переход к деталям подписки
    router.push({ name: 'my-subscription-detail', params: { id: subscriptionId } })
  }
}

function extendSubscription(subscription: Subscription) {
  console.log('🔄 extendSubscription called with:', subscription)
  
  // Отправляем отчет в Sentry
  SentryService.trackButtonClick('Продлить подписку', {
    subscription_id: subscription.id,
    exp_date: subscription.exp_date,
    is_active: subscription.is_active
  })
  
  // Устанавливаем текущую подписку для продления
  subscriptionStore.setCurrentSubscription(subscription)
  console.log('🔄 setCurrentSubscription called')
  
  // Переходим на страницу оплаты для продления
  sessionStorage.setItem('purchaseType', 'extension')
  sessionStorage.setItem('cameFromHome', 'true')
  console.log('🔄 sessionStorage items set:', {
    purchaseType: sessionStorage.getItem('purchaseType'),
    cameFromHome: sessionStorage.getItem('cameFromHome')
  })
  
  router.push('/payment')
  console.log('🔄 navigating to /payment')
}

async function addNewSubscription() {
  // Добавляем вибрацию для новой подписки
  await vibratePattern('success')
  
  // Отправляем отчет в Sentry
  SentryService.trackButtonClick('Добавить новую подписку', {
    existing_subscriptions_count: state.subscriptions?.length || 0
  })
  
  // Переходим на страницу оплаты для новой подписки
  sessionStorage.setItem('purchaseType', 'new')
  sessionStorage.setItem('cameFromHome', 'true')
  router.push('/payment')
}

async function goToSetup(subscriptionId: string) {
  // Добавляем вибрацию
  await vibratePattern('button')
  
  console.log('⚙️ MySubscriptions: goToSetup called for subscription:', subscriptionId)
  
  // Отправляем отчет в Sentry
  SentryService.trackButtonClick('Настроить подписку', {
    subscription_id: subscriptionId
  })
  
  // Переходим на страницу установки с ID подписки
  router.push(`/setup/${subscriptionId}`)
}

// Флаг для предотвращения множественных инициализаций
let isInitialized = false

onMounted(async () => {
    if (isInitialized) {
        console.log('📋 MySubscriptions: Уже инициализирован, пропускаем')
        return
    }
    
    isInitialized = true
    console.log('📋 MySubscriptions mounted')
    
    // Отправляем отчет о просмотре страницы подписок
    SentryService.trackPageView('Список подписок', {
        is_extension_flow: sessionStorage.getItem('isExtensionFlow') === 'true',
        existing_subscriptions_count: subscriptionStore.subscriptions.length
    })
    
    // Проверяем контекст захода на страницу
    const isExtensionFlow = sessionStorage.getItem('isExtensionFlow')
    if (isExtensionFlow === 'true') {
        console.log('📋 Пользователь пришел для выбора подписки для продления')
    } else {
        console.log('📋 Обычный просмотр подписок')
    }
    
    // Загружаем подписки только если их еще нет или если нужны все подписки
    if (!subscriptionStore.subscriptions.length || subscriptionStore.subscriptions.length === 0) {
        console.log('📋 Загружаем список всех подписок')
        try {
            await subscriptionStore.fetchSubscription(true)
        } catch (error) {
            console.error('📋 Ошибка загрузки подписок:', error)
        }
    } else {
        console.log('📋 Подписки уже загружены:', subscriptionStore.subscriptions.length)
    }
    
    console.log(state.subscriptions)
})

onUnmounted(() => {
    console.log('📋 MySubscriptions unmounted')
    isInitialized = false
    
    const isExtensionFlow = sessionStorage.getItem('isExtensionFlow')
    if (isExtensionFlow === 'true') {
        console.log('📋 Очищаем флаг isExtensionFlow при уходе со страницы')
        sessionStorage.removeItem('isExtensionFlow')
    }
})
</script>

<style lang="scss">
.my-subscriptions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    padding: 40px 20px;
    box-sizing: border-box;
    overflow-y: hidden;
    overflow-x: hidden;

    &-top-row {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;

        &__title {
            &-text {
                @include h1;
            }
        }

        &__having-subs {
            @include p2-regular;
            opacity: 0.4;
            margin-top: 12px;
        }

        &-subs-list {
            display: flex;
            flex-direction: column;
            margin-top: 28px;
            gap: 8px;
            width: 100%;
        }

        &-subs-card {
            background: $secondary-200;
            border-radius: 12px;
            padding: 12px 8px 12px 20px;
            display: flex;
            transition: background 0.3s;

            &:hover {
                background: $secondary-200-active;
                cursor: pointer;
            }

            &--policy {
                padding: 8px 8px 8px 20px;
                min-height: 52px;
                align-items: center;

                .my-subscriptions-top-row-subs-card-row-main-info {
                    justify-content: center;
                }

                svg {
                    margin-left: auto;
                    flex-shrink: 0;
                }
            }

            &-row {
                width: 100%;
                display: flex;
                flex-direction: column;

                &__id {
                    @include p2-regular;
                }

                &__exp {
                    @include p2-regular;
                    opacity: 0.4;
                }

                &-main-info {
                    flex-basis: 50%;
                    gap: 4px;
                }

                &-sub-info {
                    flex-basis: 50%;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 8px;
                }

                .subscription-actions {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }

                &__info-warning-card {
                    button {
                        @include p3-regular;
                        width: 115px;
                        height: 18px;
                        box-sizing: border-box;
                    }
                }
            }
        }
    }

    &-bottom-row {
        &__btn {
            @include p2-regular;
            cursor: pointer;
        }
    }
}
</style>